import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCrmApi } from '@/services/api';
import { t } from '@/i18n';

interface Props {
  nip: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  nip: string;
  sourceName: string;
  message: string;
}

const EMPTY_FORM: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  nip: '',
  sourceName: '',
  message: '',
};

/**
 * Manual lead entry — a phone call, a conference contact, an inbox forward.
 * Goes through the same intake as the public API, so a manually entered lead
 * gets the same domain matching and lands in the same queue.
 */
export function NewLeadDialog({ nip, open, onOpenChange, onCreated }: Props) {
  const { createLead } = useCrmApi();
  const [form, setForm] = useState<FormValues>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY_FORM);
  }, [open]);

  const set = (key: keyof FormValues) => (event: { target: { value: string } }) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  // The backend refuses a lead with no way to reach the sender; mirroring the
  // rule here turns a 422 into a disabled button.
  const canSubmit = form.email.trim() !== '' || form.phone.trim() !== '';

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await createLead(nip, {
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        companyName: form.companyName.trim() || null,
        nip: form.nip.trim() || null,
        message: form.message.trim() || null,
        sourceName: form.sourceName.trim() || 'manual',
      });
      toast.success(t('crm.leads.created'));
      onCreated();
    } catch (error) {
      toast.error((error as Error)?.message || t('errors.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('crm.leads.newLead')}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Imię</Label>
            <Input value={form.firstName} onChange={set('firstName')} />
          </div>
          <div className="space-y-1.5">
            <Label>Nazwisko</Label>
            <Input value={form.lastName} onChange={set('lastName')} />
          </div>
          <div className="space-y-1.5">
            <Label>E-mail</Label>
            <Input value={form.email} onChange={set('email')} type="email" />
          </div>
          <div className="space-y-1.5">
            <Label>Telefon</Label>
            <Input value={form.phone} onChange={set('phone')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.leads.columns.company')}</Label>
            <Input value={form.companyName} onChange={set('companyName')} />
          </div>
          <div className="space-y-1.5">
            <Label>NIP</Label>
            <Input value={form.nip} onChange={set('nip')} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('crm.leads.columns.source')}</Label>
            <Input value={form.sourceName} onChange={set('sourceName')} placeholder="manual" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('crm.leads.columns.message')}</Label>
            <Textarea value={form.message} onChange={set('message')} rows={4} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
